import { Button } from '@/components/Button';
import { CardOverview } from '@/components/CardOverview';
import { Modal } from '@/components/Modal';
import { Text } from '@/components/Text';
import { useAuthStore } from '@/stores/useAuthStore';
import { useCardStore } from '@/stores/useCardStore';
import { Card } from '@/types/card';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPlus, FaStar, FaUser } from 'react-icons/fa';

interface SelectEventCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectCard: (cardSlug: string) => void;
  onCreateCard: () => void;
}

export const SelectEventCardModal = ({
  isOpen,
  onClose,
  onSelectCard,
  onCreateCard,
}: SelectEventCardModalProps) => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { listCards, cards, loading } = useCardStore();
  const [selectedCard, setSelectedCard] = useState<Card | null>(null);

  // Reset inputs when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedCard(null);
    }
  }, [isOpen]);

  // Load cards when modal opens
  useEffect(() => {
    if (isOpen) {
      const fetchCards = async () => {
        await listCards({
          user_id: user?.id,
          paginate: 0,
        });
      };

      fetchCards();
    }
  }, [isOpen, listCards, user?.id]);

  const handleClose = () => {
    setSelectedCard(null);
    onClose();
  };

  const handleSelectCard = () => {
    if (selectedCard) {
      onSelectCard(selectedCard.slug);
      handleClose();
    }
  };

  const handleCreateNewCard = () => {
    onCreateCard();
    handleClose();
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3 },
    },
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title=""
      className="max-h-[90vh] w-full max-w-2xl"
    >
      <motion.div
        className="flex h-full max-h-[80vh] flex-col"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Fixed Header */}
        <motion.div
          className="shrink-0 border-b border-gray-100 pb-4"
          variants={itemVariants}
        >
          <div className="flex items-center gap-2">
            <div className="flex size-12 items-center justify-center rounded-lg bg-gradient-to-br from-linko-light to-linko-dark">
              <FaStar className="size-6 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">
              {t('eventDetails:card.modal.chooseYourCard')}
            </h2>
          </div>
        </motion.div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto py-4">
          <div className="space-y-4">
            {/* Create New Card Option */}
            <motion.div variants={itemVariants}>
              <button
                onClick={handleCreateNewCard}
                className="w-full rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-4 text-center"
              >
                <div className="flex flex-col items-center gap-3 sm:flex-row sm:text-left">
                  <div className="flex size-12 items-center justify-center rounded-lg bg-gradient-to-br from-linko-light to-linko-dark">
                    <FaPlus className="size-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {t('eventDetails:card.createNew')}
                    </h3>
                    <p className="mt-1 text-sm text-gray-600">
                      {t('eventDetails:card.createNewDescription')}
                    </p>
                  </div>
                </div>
              </button>
            </motion.div>

            {/* Divider */}
            <motion.div
              className="flex items-center gap-4"
              variants={itemVariants}
            >
              <div className="h-px flex-1 bg-gray-200" />
              <Text className="text-sm font-medium text-gray-500">
                {t('eventDetails:card.modal.or')}
              </Text>
              <div className="h-px flex-1 bg-gray-200" />
            </motion.div>

            {/* Existing Cards Section */}
            <motion.div variants={itemVariants}>
              <div className="mb-3 flex items-center gap-3">
                <div className="flex size-12 items-center justify-center rounded-lg bg-gradient-to-br from-linko-light to-linko-dark">
                  <FaUser className="size-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t('eventDetails:card.selectExisting')}
                  </h3>
                  <Text className="text-sm text-gray-600">
                    {t('eventDetails:card.selectExistingDescription')}
                  </Text>
                </div>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="flex flex-col items-center gap-3">
                    <div className="size-8 animate-spin rounded-full border-2 border-linko-dark border-t-transparent" />
                    <Text className="text-sm text-gray-500">
                      {t('eventDetails:card.modal.loadingCards')}
                    </Text>
                  </div>
                </div>
              ) : cards.length === 0 ? (
                <div className="rounded-lg border-2 border-dashed border-gray-200 p-6 text-center">
                  <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-full bg-gray-100">
                    <FaUser className="size-6 text-gray-400" />
                  </div>
                  <h4 className="text-lg font-medium text-gray-900">
                    {t('eventDetails:card.modal.noCardsFound')}
                  </h4>
                  <p className="mt-1 text-sm text-gray-600">
                    {t('eventDetails:card.modal.noCardsYet')}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {cards.map((card: Card) => (
                    <div
                      key={card.slug}
                      className={`relative cursor-pointer rounded-lg border-2 p-4 ${
                        selectedCard?.slug === card.slug
                          ? 'border-linko-dark bg-linko-light/10'
                          : 'border-gray-200 bg-white'
                      }`}
                      onClick={() => setSelectedCard(card)}
                    >
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
                        <div className="flex gap-4 lg:flex-1">
                          <div className="min-w-0 flex-1">
                            <h4 className="truncate text-lg font-semibold text-gray-900">
                              {card.name}
                            </h4>
                            <p className="truncate text-sm text-gray-600">
                              {card.company ||
                                t('eventDetails:card.modal.noCompany')}
                            </p>
                          </div>
                        </div>
                        <div className="flex justify-center lg:w-80 lg:shrink-0">
                          <div className="w-full max-w-xs lg:max-w-none">
                            <CardOverview card={card} />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </div>
        </div>

        {/* Fixed Footer */}
        <motion.div
          className="shrink-0 border-t border-gray-100 pt-4"
          variants={itemVariants}
        >
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button
              onClick={handleClose}
              className="flex-1 border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
            >
              {t('common:cancel')}
            </Button>
            <Button
              onClick={handleSelectCard}
              disabled={!selectedCard}
              className="flex-1 bg-gradient-to-r from-linko-dark to-linko-light text-white shadow-md transition-all duration-300 hover:shadow-lg disabled:from-gray-400 disabled:to-gray-500"
            >
              {selectedCard
                ? t('eventDetails:card.modal.selectCard', {
                    cardName: selectedCard.name,
                  })
                : t('eventDetails:card.select')}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </Modal>
  );
};
